import React, { useState, useEffect } from "react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import Spinner from "../../assets/components/spinner";
import axios from "axios";
import globalVariables from "../../GlobalVariable";
import { Check, CreditCard } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useBusiness } from "../../context/BusinessContext";
import api from "../../utils/api";

const SuccessModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
        <div className="flex justify-center items-center w-16 h-16 mx-auto bg-green-100 rounded-full border-4 border-green-200">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mt-6 mb-2">
          Upgrade Complete!
        </h2>
        <p className="text-gray-600 mb-6">
          Congratulations! Your plan has been upgraded successfully.
        </p>
        <button
          onClick={onClose}
          className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 text-white font-semibold py-3 px-4 rounded-md shadow-md"
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
};

const StripeInputWrapper = ({ component: Component, error, ...props }) => {
  const wrapperClasses = `p-3 border rounded-md shadow-sm transition-all bg-white ${
    error
      ? "border-red-500 ring-1 ring-red-500"
      : "border-gray-300 focus-within:ring-2 focus-within:ring-blue-500"
  }`;
  return (
    <div className={wrapperClasses}>
      {" "}
      <Component {...props} />{" "}
    </div>
  );
};

// Componente para mostrar el icono de la tarjeta
const CardBrandIcon = ({ brand }) => {
  if (!brand) return <CreditCard className="w-5 h-5 text-gray-400" />;

  const getCardIcon = () => {
    switch (brand) {
      case "visa":
        return (
          <div className="flex items-center">
            <div className="w-8 h-5 bg-gradient-to-br from-blue-600 to-blue-800 rounded flex items-center justify-center shadow-sm">
              <span className="text-white text-xs font-bold tracking-wider">
                VISA
              </span>
            </div>
          </div>
        );
      case "mastercard":
        return (
          <div className="flex items-center">
            <div className="w-8 h-5 bg-gradient-to-r from-red-500 via-red-600 to-orange-500 rounded flex items-center justify-center shadow-sm relative overflow-hidden">
              <div className="w-2 h-2 bg-white rounded-full mr-1 opacity-90"></div>
              <div className="w-2 h-2 bg-white rounded-full opacity-90"></div>
            </div>
          </div>
        );
      case "amex":
        return (
          <div className="flex items-center">
            <div className="w-8 h-5 bg-gradient-to-br from-blue-500 to-blue-700 rounded flex items-center justify-center shadow-sm">
              <span className="text-white text-xs font-bold tracking-wider">
                AMEX
              </span>
            </div>
          </div>
        );
      case "discover":
        return (
          <div className="flex items-center">
            <div className="w-8 h-5 bg-gradient-to-br from-orange-500 to-red-500 rounded flex items-center justify-center shadow-sm">
              <span className="text-white text-xs font-bold tracking-wider">
                DISC
              </span>
            </div>
          </div>
        );
      case "jcb":
        return (
          <div className="flex items-center">
            <div className="w-8 h-5 bg-gradient-to-br from-blue-600 to-blue-800 rounded flex items-center justify-center shadow-sm">
              <span className="text-white text-xs font-bold tracking-wider">
                JCB
              </span>
            </div>
          </div>
        );
      case "diners":
        return (
          <div className="flex items-center">
            <div className="w-8 h-5 bg-gradient-to-br from-gray-600 to-gray-800 rounded flex items-center justify-center shadow-sm">
              <span className="text-white text-xs font-bold tracking-wider">
                DIN
              </span>
            </div>
          </div>
        );
      default:
        return <CreditCard className="w-5 h-5 text-gray-400" />;
    }
  };

  return getCardIcon();
};

const UpgradeCheckoutForm = ({ clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [cardErrors, setCardErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [cardBrand, setCardBrand] = useState(null);

  useEffect(() => {
    if (user?.email) {
      setUserEmail(user.email);
    }
    // Limpiar marca de tarjeta al montar el componente
    setCardBrand(null);
  }, [user]);

  // Función para detectar la marca de tarjeta basada en el número
  const detectCardBrand = (cardNumber) => {
    if (!cardNumber) return null;

    // Remover espacios y caracteres no numéricos
    const cleanNumber = cardNumber.replace(/\s/g, "");

    // Patrones para detectar marcas de tarjeta
    if (/^4/.test(cleanNumber)) return "visa";
    if (/^5[1-5]/.test(cleanNumber)) return "mastercard";
    if (/^3[47]/.test(cleanNumber)) return "amex";
    if (/^6/.test(cleanNumber)) return "discover";
    if (/^35/.test(cleanNumber)) return "jcb";
    if (/^3[0-9]/.test(cleanNumber)) return "diners";

    return null;
  };

  const handleStripeElementChange = (elementName) => (event) => {
    setCardErrors((prev) => ({
      ...prev,
      [elementName]: event.error ? event.error.message : null,
    }));

    // Detectar marca de tarjeta cuando cambia el número
    if (elementName === "cardNumber") {
      if (event.brand) {
        // Stripe proporciona la marca de tarjeta directamente
        setCardBrand(event.brand);
      } else if (event.complete && event.value) {
        // Fallback: detectar manualmente si Stripe no proporciona la marca
        const brand = detectCardBrand(event.value);
        setCardBrand(brand);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);

    const { error: paymentError } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: { email: userEmail },
        },
      }
    );

    if (paymentError) {
      setMessage(paymentError.message);
      setLoading(false);
      return;
    }

    // Si el pago es exitoso, actualiza el plan
    const storedPlanId = sessionStorage.getItem("selectedPlanId");
    console.log(
      "Payment successful, updating plan. Stored plan ID:",
      storedPlanId,
      "User ID:",
      user._id
    );

    if (!storedPlanId || !user?._id) {
      setMessage("Critical data missing for upgrade. Please contact support.");
      setLoading(false);
      return;
    }

    try {
      // Obtener el negocio del usuario si no está disponible en el contexto
      let businessId = selectedBusiness?._id;
      let businessResponse = null;
      let businessData = null;

      console.log("Business ID from context:", businessId);

      if (!businessId) {
        console.log("Fetching business from API for user:", user._id);
        businessResponse = await api.get(`/business/by_user/${user._id}`);
        console.log("Business API response:", businessResponse.data);

        if (
          businessResponse.data?.success &&
          businessResponse.data.content?.length > 0
        ) {
          // Intentar diferentes campos para el ID del negocio
          businessData = businessResponse.data.content[0];
          businessId =
            businessData._id ||
            businessData.id ||
            businessData.business_id ||
            businessData.yelp_id;
          console.log("Business object:", businessData);
          console.log("Selected business ID:", businessId);

          if (!businessId) {
            throw new Error("Business ID not found in business object");
          }
        } else {
          throw new Error("No business found for user.");
        }
      } else {
        // Si tenemos businessId del contexto, obtener los datos del negocio para el fallback
        businessData = selectedBusiness;
      }

      // Obtener el token de autenticación
      const authToken =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      console.log("Auth token available:", !!authToken);

      if (!authToken) {
        throw new Error("Authentication token not found. Please log in again.");
      }

      // Validar que el businessId sea válido
      if (
        !businessId ||
        typeof businessId !== "string" ||
        businessId.length < 10
      ) {
        throw new Error(`Invalid business ID: ${businessId}`);
      }

      // Actualizar el plan del usuario usando el endpoint estándar
      const requestData = {
        plan_id: storedPlanId,
        _id: user._id,
      };

      const requestHeaders = {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      };

      console.log("Making PATCH request to:", `${globalVariables.domain}/user`);
      console.log("Request data:", requestData);
      console.log("Request headers:", {
        ...requestHeaders,
        Authorization: `Bearer ${authToken ? "***" : "NOT_FOUND"}`,
      });

      const response = await axios.patch(
        `${globalVariables.domain}/user`,
        requestData,
        {
          headers: requestHeaders,
        }
      );

      console.log("User plan update response:", response.data);

      if (response.data.success) {
        console.log(
          "User plan updated successfully, now updating business plan"
        );

        const today = new Date();
        const expireDate = new Date(today);
        expireDate.setMonth(expireDate.getMonth() + 1); //  +1 month

        // Actualizar también el plan del negocio
        try {
          const businessUpdateData = {
            plan_id: storedPlanId,
            contract_expires_date: expireDate.toISOString().split("T")[0], // format YYYY-MM-DD
          };

          console.log("=== BUSINESS UPDATE DEBUG ===");
          console.log("Business ID to update:", businessId);
          console.log("Business ID type:", typeof businessId);
          console.log("Business ID length:", businessId?.length);
          console.log("Plan ID to assign:", storedPlanId);
          console.log("Business update data:", businessUpdateData);
          console.log("Business update headers:", {
            Authorization: `Bearer ${authToken ? "***" : "NOT_FOUND"}`,
            "Content-Type": "application/json",
          });

          // Intentar diferentes endpoints para la actualización del negocio
          let businessUpdateResponse;
          let endpointUsed = "";

          try {
            // Primero intentar con el endpoint estándar
            endpointUsed = `${globalVariables.domain}/business/by_id/${businessId}`;
            businessUpdateResponse = await axios.patch(
              endpointUsed,
              businessUpdateData,
              {
                headers: {
                  Authorization: `Bearer ${authToken}`,
                  "Content-Type": "application/json",
                },
              }
            );
          } catch (firstAttemptError) {
            console.log("First attempt failed, trying alternative endpoint...");

            // Si falla, intentar con el campo yelp_id si está disponible
            const yelpId = businessData?.yelp_id || businessData?.yelpId;

            if (yelpId && yelpId !== businessId) {
              endpointUsed = `${globalVariables.domain}/business/${yelpId}`;
              console.log(
                "Trying alternative endpoint with yelp_id:",
                endpointUsed
              );

              businessUpdateResponse = await axios.patch(
                endpointUsed,
                businessUpdateData,
                {
                  headers: {
                    Authorization: `Bearer ${authToken}`,
                    "Content-Type": "application/json",
                  },
                }
              );
            } else {
              throw firstAttemptError;
            }
          }

          console.log(
            "Business plan update response:",
            businessUpdateResponse.data
          );
          console.log("Endpoint used for business update:", endpointUsed);
          console.log("=== END BUSINESS UPDATE DEBUG ===");

          if (businessUpdateResponse.data.success) {
            console.log("Business plan updated successfully");
            setShowSuccessModal(true);
            sessionStorage.removeItem("selectedPlanId");
            sessionStorage.removeItem("upgradePlanData");
            console.log(
              "Both user and business plans updated successfully, showing success modal"
            );
          } else {
            console.warn(
              "Business plan update failed:",
              businessUpdateResponse.data
            );
            // Aunque falle la actualización del negocio, el usuario ya tiene su plan actualizado
            setShowSuccessModal(true);
            sessionStorage.removeItem("selectedPlanId");
            sessionStorage.removeItem("upgradePlanData");
          }
        } catch (businessUpdateError) {
          console.error("=== BUSINESS UPDATE ERROR ===");
          console.error("Error updating business plan:", businessUpdateError);
          console.error(
            "Error response status:",
            businessUpdateError.response?.status
          );
          console.error(
            "Error response data:",
            businessUpdateError.response?.data
          );
          console.error("Error message:", businessUpdateError.message);
          console.error("Business ID that failed:", businessId);
          console.error("=== END BUSINESS UPDATE ERROR ===");

          // Mostrar información específica sobre el error del negocio
          if (businessUpdateError.response?.status === 401) {
            console.warn("Business update failed: Authentication failed");
          } else if (businessUpdateError.response?.status === 403) {
            console.warn("Business update failed: Permission denied");
          } else if (businessUpdateError.response?.status === 404) {
            console.warn("Business update failed: Business not found");
          } else {
            console.warn(
              "Business update failed:",
              businessUpdateError.response?.data || businessUpdateError.message
            );
          }

          // Aunque falle la actualización del negocio, el usuario ya tiene su plan actualizado
          setShowSuccessModal(true);
          sessionStorage.removeItem("selectedPlanId");
          sessionStorage.removeItem("upgradePlanData");
          console.log(
            "User plan updated but business plan update failed, still showing success modal"
          );
        }
      } else {
        throw new Error(response.data.content || "Failed to update plan.");
      }
    } catch (apiError) {
      if (apiError.response?.status === 401) {
        setMessage(
          "Authentication failed. Please log in again and try the upgrade."
        );
      } else if (apiError.response?.status === 403) {
        setMessage("You don't have permission to perform this action.");
      } else {
        setMessage(
          apiError.response?.data?.content ||
            apiError.message ||
            "An error occurred during the final step of the upgrade."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => navigate("/dashboard_client")}
      />
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-xl rounded-xl p-8 space-y-6"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Account Email
          </label>
          <input
            type="email"
            value={userEmail}
            disabled
            className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm bg-gray-100"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Card Number
          </label>
          <div className="relative">
            <StripeInputWrapper
              component={CardNumberElement}
              onChange={handleStripeElementChange("cardNumber")}
              error={cardErrors.cardNumber}
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <CardBrandIcon brand={cardBrand} />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiration
            </label>
            <StripeInputWrapper
              component={CardExpiryElement}
              onChange={handleStripeElementChange("cardExpiry")}
              error={cardErrors.cardExpiry}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              CVC
            </label>
            <StripeInputWrapper
              component={CardCvcElement}
              onChange={handleStripeElementChange("cardCvc")}
              error={cardErrors.cardCvc}
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading || !stripe}
          className="w-full flex items-center justify-center bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold py-3 px-4 rounded-md shadow-md"
        >
          {loading ? <Spinner /> : "Pay and Upgrade"}
        </button>
        {message && (
          <div className="mt-4 p-4 text-sm text-red-800 bg-red-50 rounded-md">
            {message}
          </div>
        )}
      </form>
    </>
  );
};

export default UpgradeCheckoutForm;
